import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { outcomesApi, type OutcomesResponse } from '@/lib/api';
import { StatCard } from '@/components/ui/StatCard';
import { cn } from '@/lib/utils';
import { WarningCircle, Gavel, Trophy, PiggyBank, Clock } from '@phosphor-icons/react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
}

function formatPercent(rate: number): string {
  return `${rate.toFixed(1)}%`;
}

function outcomeLabel(outcome: string): string {
  if (outcome === 'won') return 'Won';
  if (outcome === 'lost') return 'Lost';
  if (outcome === 'expired') return 'Expired';
  return outcome;
}

function OutcomeBadge({ outcome }: { outcome: string }) {
  const base = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold';
  if (outcome === 'won') return <span className={cn(base, 'bg-green-100 text-green-700')}>Won</span>;
  if (outcome === 'lost') return <span className={cn(base, 'bg-red-100 text-red-700')}>Lost</span>;
  return <span className={cn(base, 'bg-slate-100 text-slate-500')}>{outcomeLabel(outcome)}</span>;
}

// ---------------------------------------------------------------------------
// Period selector
// ---------------------------------------------------------------------------

const PERIODS = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'all', label: 'All time' },
];

// ---------------------------------------------------------------------------
// Weekly trend bar chart
// ---------------------------------------------------------------------------

function WeeklyTrendChart({ buckets }: { buckets: Record<string, { won: number; lost: number }> }) {
  const entries = Object.entries(buckets)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-12);

  if (entries.length === 0) {
    return <p className="text-sm text-slate-400 italic">No weekly data available.</p>;
  }

  const maxTotal = Math.max(...entries.map(([, v]) => v.won + v.lost), 1);

  return (
    <div className="flex items-end gap-2 h-40">
      {entries.map(([week, v]) => {
        const total = v.won + v.lost;
        const wonPct = total > 0 ? (v.won / maxTotal) * 100 : 0;
        const lostPct = total > 0 ? (v.lost / maxTotal) * 100 : 0;
        const shortLabel = week.replace(/^\d{4}-W/, 'W');
        return (
          <div key={week} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex flex-col justify-end" style={{ height: '120px' }}>
              {lostPct > 0 && (
                <div
                  className="w-full bg-red-400 transition-all"
                  style={{ height: `${lostPct}%` }}
                  title={`Lost: ${v.lost}`}
                />
              )}
              {wonPct > 0 && (
                <div
                  className="w-full bg-green-500 rounded-t transition-all"
                  style={{ height: `${wonPct}%` }}
                  title={`Won: ${v.won}`}
                />
              )}
              {wonPct === 0 && lostPct === 0 && (
                <div className="w-full bg-slate-100 rounded-t" style={{ height: '4px' }} />
              )}
            </div>
            <span className="text-[10px] text-slate-400 font-medium">{shortLabel}</span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page skeleton
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 w-48 bg-slate-200 rounded" />
      <div className="grid grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 h-28" />
        ))}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 h-48" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function OutcomesPage() {
  const [period, setPeriod] = useState('30d');

  const { data: res, isLoading, error } = useQuery({
    queryKey: ['admin-outcomes', period],
    queryFn: () => outcomesApi.getOutcomes(period),
  });

  const data: OutcomesResponse | null = res?.data ?? null;
  const apiError = res?.error ?? (error ? String(error) : null);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Outcomes</h2>
          <p className="text-slate-500 text-sm mt-1">
            Dispute win/loss tracking, recovery amounts, and processor performance.
          </p>
        </div>
        {/* Period selector */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={cn(
                'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                period === p.value
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error state */}
      {apiError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <WarningCircle size={18} weight="regular" className="text-red-500" />
          <p className="text-sm text-red-700">{apiError}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading && <LoadingSkeleton />}

      {/* Content */}
      {!isLoading && data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-4">
            <StatCard
              label="Total Outcomes"
              value={data.summary.total.toLocaleString()}
              icon={Gavel}
              iconColor="text-primary"
            />
            <StatCard
              label="Win Rate"
              value={formatPercent(data.summary.winRate)}
              icon={Trophy}
              iconColor={data.summary.winRate >= 50 ? 'text-green-600' : 'text-red-500'}
              change={`${data.summary.won}W / ${data.summary.lost}L`}
              changeColor={data.summary.winRate >= 50 ? 'text-green-500' : 'text-red-500'}
            />
            <StatCard
              label="Amount Recovered"
              value={formatCurrency(data.summary.amountRecovered)}
              icon={PiggyBank}
              iconColor="text-green-600"
            />
            <StatCard
              label="Avg Days to Outcome"
              value={data.summary.avgDaysToOutcome.toFixed(1)}
              icon={Clock}
              iconColor="text-blue-600"
            />
          </div>

          {/* Two-column: by processor + weekly trend */}
          <div className="grid grid-cols-2 gap-4">
            {/* By Processor */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100">
                <h3 className="text-base font-bold text-slate-900">By Processor</h3>
              </div>
              {Object.keys(data.byProcessor).length === 0 ? (
                <p className="px-6 py-8 text-sm text-slate-400 italic">No processor data.</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="text-xs text-slate-400 font-medium border-b border-slate-100 bg-slate-50/50">
                      <th className="text-left px-6 py-3">Processor</th>
                      <th className="text-right px-6 py-3">Wins</th>
                      <th className="text-right px-6 py-3">Losses</th>
                      <th className="text-right px-6 py-3">Win Rate</th>
                      <th className="text-right px-6 py-3">Recovered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.byProcessor).map(([processor, stats]) => {
                      const total = stats.wins + stats.losses;
                      const rate = total > 0 ? (stats.wins / total) * 100 : 0;
                      return (
                        <tr
                          key={processor}
                          className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition"
                        >
                          <td className="px-6 py-3">
                            <span className="text-sm font-semibold text-slate-800">{processor}</span>
                          </td>
                          <td className="px-6 py-3 text-sm text-green-600 font-medium text-right">
                            {stats.wins.toLocaleString()}
                          </td>
                          <td className="px-6 py-3 text-sm text-red-500 font-medium text-right">
                            {stats.losses.toLocaleString()}
                          </td>
                          <td className="px-6 py-3 text-right">
                            <span
                              className={cn(
                                'text-sm font-semibold',
                                rate >= 50 ? 'text-green-600' : 'text-red-500',
                              )}
                            >
                              {formatPercent(rate)}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-sm text-slate-700 text-right">
                            {formatCurrency(stats.recovered)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Weekly Trend */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-base font-bold text-slate-900 mb-1">Weekly Trend</h3>
              <p className="text-xs text-slate-400 mb-4">
                Last 12 weeks — <span className="inline-block w-2.5 h-2.5 rounded-sm bg-green-500 align-middle" /> won{' '}
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-400 align-middle ml-1" /> lost
              </p>
              <WeeklyTrendChart buckets={data.weeklyBuckets} />
            </div>
          </div>

          {/* Top Reason Codes */}
          {data.topReasonCodes.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100">
                <h3 className="text-base font-bold text-slate-900">Top Reason Codes</h3>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-slate-400 font-medium border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left px-6 py-3">Reason Code</th>
                    <th className="text-right px-6 py-3">Total</th>
                    <th className="text-right px-6 py-3">Wins</th>
                    <th className="text-right px-6 py-3">Losses</th>
                    <th className="text-right px-6 py-3">Win Rate</th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {data.topReasonCodes.map((rc) => (
                    <tr
                      key={rc.code}
                      className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition"
                    >
                      <td className="px-6 py-3">
                        <span className="font-mono text-sm font-semibold text-slate-800">{rc.code}</span>
                      </td>
                      <td className="px-6 py-3 text-sm text-slate-700 text-right">{rc.total.toLocaleString()}</td>
                      <td className="px-6 py-3 text-sm text-green-600 font-medium text-right">
                        {rc.wins.toLocaleString()}
                      </td>
                      <td className="px-6 py-3 text-sm text-red-500 font-medium text-right">
                        {rc.losses.toLocaleString()}
                      </td>
                      <td className="px-6 py-3 text-right">
                        <span
                          className={cn(
                            'text-sm font-semibold',
                            rc.winRate >= 50 ? 'text-green-600' : 'text-red-500',
                          )}
                        >
                          {formatPercent(rc.winRate)}
                        </span>
                      </td>
                      <td className="px-6 py-3">
                        {/* Mini win-rate bar */}
                        <div className="flex items-center gap-1">
                          <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                            <div
                              className={cn(
                                'h-full rounded-full',
                                rc.winRate >= 50 ? 'bg-green-500' : 'bg-red-400',
                              )}
                              style={{ width: `${rc.winRate}%` }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Recent Outcomes */}
          {data.recentOutcomes.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-900">Recent Outcomes</h3>
                <span className="text-xs text-slate-400">{data.recentOutcomes.length} results</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-xs text-slate-400 font-medium border-b border-slate-100 bg-slate-50/50">
                      <th className="text-left px-6 py-3">Case ID</th>
                      <th className="text-left px-6 py-3">Customer</th>
                      <th className="text-left px-6 py-3">Processor</th>
                      <th className="text-right px-6 py-3">Amount</th>
                      <th className="text-left px-6 py-3">Outcome</th>
                      <th className="text-right px-6 py-3">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recentOutcomes.map((ro) => (
                      <tr
                        key={ro.caseId}
                        className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition"
                      >
                        <td className="px-6 py-3">
                          <span className="font-mono text-xs text-slate-600">{ro.caseId}</span>
                        </td>
                        <td className="px-6 py-3 text-sm text-slate-700">{ro.customer || '—'}</td>
                        <td className="px-6 py-3 text-sm text-slate-500">{ro.processor}</td>
                        <td className="px-6 py-3 text-sm text-slate-800 font-medium text-right">
                          {formatCurrency(ro.amount)}
                        </td>
                        <td className="px-6 py-3">
                          <OutcomeBadge outcome={ro.outcome} />
                        </td>
                        <td className="px-6 py-3 text-xs text-slate-400 text-right whitespace-nowrap">
                          {ro.outcomeDate ? new Date(ro.outcomeDate * 1000).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Empty state when no outcomes at all */}
          {data.summary.total === 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Gavel size={40} weight="regular" className="text-slate-300 mx-auto" />
              <p className="text-slate-500 mt-3 text-sm">No outcomes recorded for this period.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/outcomes')({
  component: OutcomesPage,
});
